import React, { useCallback, useMemo } from 'react';
import { connect, shallowEqual, useSelector } from 'react-redux';
import { ScrollContainer } from '@beef/legacy-ui-kit';
import { useAwaitedList } from '@beef/shop-common';
import { CarouselWithNavigation } from '@beef/smart-kit';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';

import { Col, CollectionMenuItem, ContentLiner, Grid } from 'bricks';
import { NestedCollections } from 'pages/_PageLayout/components/Navigation/components/NestedCollections';
import pushEvent, { EVENT_NAVIGATION_ITEM_VIEW } from 'pushEvent';
import { selectIsMobileView } from 'store/common/mobileView/selectors';
import { processNavigationItemClick } from 'store/common/navigation/actions';
import { getBreadcrumbs, getMainCollections } from 'store/common/navigation/selectors';
import {
  isScreenBelow640,
  isScreenBelow1004,
  isScreenBelow1280,
} from 'store/common/settings/breakpoint/selectors';
import { onNestedCollectionChange } from 'store/ProductCatalog/actions';
import commonStyles from 'styles/common.pcss';

import styles from './styles.pcss';

const cx = classNames.bind({ ...commonStyles, ...styles });

const Navigation_ = ({ className, handleMenuItemClick, onCollectionChange, onlyCrumbs }) => {
  const { breadcrumbs, collections, isBelow1280, isMobile, isMobileView, isTablet } = useSelector(
    (state) => ({
      breadcrumbs: getBreadcrumbs(state),
      collections: getMainCollections(state),
      isBelow1280: isScreenBelow1280(state),
      isMobile: isScreenBelow640(state),
      isMobileView: selectIsMobileView(state),
      isTablet: isScreenBelow1004(state),
    }),
    shallowEqual,
  );

  const [setItem] = useAwaitedList(EVENT_NAVIGATION_ITEM_VIEW, pushEvent);

  const handleViewed = useCallback(
    (data) => {
      setItem(data);
    },
    [setItem],
  );

  const nestedCollections = useMemo(() => {
    const { activeSlug, list } = collections;

    const activeCollections = list.find(({ slug }) => slug === activeSlug);

    if (activeCollections) {
      const tabList = activeCollections?.collections?.list ?? [];

      const tabs = tabList.map((item) => ({
        id: item.url,
        title: item.name,
      }));

      return {
        activeSlug: `${activeCollections?.url}${activeCollections?.collections?.activeSlug}/`,
        list: tabs,
      };
    }

    return null;
  }, [collections]);

  const handleTabChange = useCallback((url) => {
    onCollectionChange({ tabs: nestedCollections.list, url });
  }, []);

  return (
    <div className={cx('desktop', className)}>
      {!isMobileView && (
        <ContentLiner className={cx('padh-s')} isUnderlay size={['xl']}>
          <Grid>
            <Col size={12}>
              <div className={cx('crumbs', isMobile && 'crumbsmob')}>
                <ScrollContainer className={cx('scrolly')} animationSteps={[]}>
                  {breadcrumbs.map(({ title, url }) => (
                    <a className={cx('crumb')} href={url} key={url}>
                      {title}
                    </a>
                  ))}
                </ScrollContainer>
              </div>
            </Col>
          </Grid>
        </ContentLiner>
      )}
      {!onlyCrumbs && (
        <>
          <div className={cx('mar-b-m')}>
            {isBelow1280 ?
              <ScrollContainer className={cx('scrolly')} animationSteps={[]}>
                <div className={cx('container', isBelow1280 && 'ctabl', isMobile && 'cmob')}>
                  <div className={cx('collections')}>
                    {collections.list.map(({ name, slug, url }, index) => (
                      <CollectionMenuItem
                        href={url}
                        iconName={slug}
                        isActive={collections.activeSlug === slug}
                        isMobile={isMobile}
                        isTablet={isTablet}
                        key={slug}
                        onClick={() => handleMenuItemClick({ name, position: index, url })}
                        onViewed={() => handleViewed({ name, position: index, url })}
                        text={name}
                      />
                    ))}
                  </div>
                </div>
              </ScrollContainer>
            : <div className={cx('container')}>
                <CarouselWithNavigation
                  nextBtnClassname={cx('next-nav')}
                  paginatePadding={false}
                  prevBtnClassname={cx('prev-nav')}
                  settings={{
                    navigation: false,
                    preloadImages: false,
                    slidesPerGroup: 9,
                    slidesPerView: 'auto',
                  }}
                  slideWidthAuto
                >
                  {collections.list.map(({ name, slug, url }, index) => (
                    <CollectionMenuItem
                      href={url}
                      iconName={slug}
                      isActive={collections.activeSlug === slug}
                      isMobile={isMobile}
                      isTablet={isTablet}
                      key={slug}
                      onClick={() => handleMenuItemClick({ name, position: index + 1, url })}
                      onViewed={() => handleViewed({ name, position: index + 1, url })}
                      text={name}
                    />
                  ))}
                </CarouselWithNavigation>
              </div>
            }
          </div>
          {Boolean(nestedCollections?.list) && (
            <div className={cx('subCollection', isMobile && 'isMobile')} id="nestedCollection">
              <NestedCollections
                isMobile={isBelow1280}
                onChange={handleTabChange}
                selectedTab={nestedCollections.activeSlug}
                tabs={nestedCollections.list}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

Navigation_.propTypes = {
  className: PropTypes.string,
  handleMenuItemClick: PropTypes.func,
  onlyCrumbs: PropTypes.bool,
};

const mapDispatchToProps = {
  handleMenuItemClick: processNavigationItemClick,
  onCollectionChange: onNestedCollectionChange,
};

export const Navigation = connect(null, mapDispatchToProps)(Navigation_);
